import {TextInput} from '@mantine/core';
import {Switch} from 'src/design/atoms/Switch';
import type {MRT_ColumnDef} from 'mantine-react-table';
import React, {useMemo, useState} from 'react';
import type {DocumentReviewRoundListItemResponse} from 'src/api/ProactApiClient';
import {
  DocumentReviewRequestStateEnum,
  DocumentReviewTypeEnum,
} from 'src/api/ProactApiClient';
import {CompletionProgress} from 'src/design/atoms/CompletionProgress';
import {Pill} from 'src/design/atoms/Pill';
import {TableRowActions} from 'src/design/molecules/TableRowActions';
import {TableToolbar} from 'src/design/molecules/TableToolbar';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {useGetDocumentReviewRoundsListQuery} from 'src/redux/queries/documentReview';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import useDebounce from 'src/shared/hooks/useDebounce';
import {
  DocumentReviewRoundStatePill,
  formatReviewDate,
} from '../../documentReviewHelpers';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

/**
 * Server-paged list of every document Acknowledgment review round, across
 * all documents/folders — used by the Acknowledgements tab of the Document
 * Management list page.
 */
export const DocumentAcknowledgementsTab: React.FC = () => {
  const navigate = useAppNavigate();
  const [searchText, setSearchText] = useState('');
  const debouncedSearchText = useDebounce(searchText, 300);
  const [incompleteOnly, setIncompleteOnly] = useState(true);

  const columns = useMemo<MRT_ColumnDef<DocumentReviewRoundListItemResponse>[]>(
    () => [
      {
        id: 'documentName',
        header: 'Document',
        enableSorting: false,
        Cell: ({row}) => row.original.documentName,
      },
      {
        accessorKey: 'documentVersion',
        header: 'Version',
        enableSorting: false,
        size: 80,
      },
      {
        id: 'state',
        header: 'Status',
        enableSorting: false,
        Cell: ({row}) => <Pill {...DocumentReviewRoundStatePill(row.original.state)} />,
      },
      {
        id: 'progress',
        header: 'Progress',
        enableSorting: false,
        Cell: ({row}) => (
          <CompletionProgress
            completed={row.original.completed}
            total={row.original.total}
          />
        ),
      },
      {
        accessorKey: 'createdAt',
        header: 'Requested date',
        enableSorting: false,
        Cell: ({row}) => formatReviewDate(row.original.createdAt),
      },
    ],
    []
  );

  const tableState = useTableState({
    identifier: 'document-acknowledgements-tab',
    columns,
    defaultDensity: 'xs',
    defaultPagination: {pageIndex: 0, pageSize: 10},
    defaultShowColumnFilters: false,
  });
  const {start, length} = tableState;

  const {data, isLoading, isFetching, isError, refetch} =
    useGetDocumentReviewRoundsListQuery(
      {
        start,
        length,
        reviewType: DocumentReviewTypeEnum.Acknowledgment,
        states: incompleteOnly
          ? [DocumentReviewRequestStateEnum.Pending]
          : undefined,
        searchText: debouncedSearchText || undefined,
      },
      {refetchOnMountOrArgChange: true}
    );

  const rows = data?.data ?? [];
  const totalCount = data?.totalRecords ?? 0;

  const {table} = useTable({
    ...tableState,
    tableOptions: {
      manualSorting: true,
      manualFiltering: true,
      manualPagination: true,
      enableColumnFilters: false,
      enableSorting: false,
      columns,
      data: rows,
      getRowId: row => String(row.id),
      renderTopToolbarCustomActions: () => (
        <TableToolbar>
          <TableToolbar.Filters>
            <TextInput
              value={searchText}
              onChange={event => setSearchText(event.currentTarget.value)}
              placeholder="Search documents..."
              w={220}
            />
            <Switch
              label="Incomplete only"
              checked={incompleteOnly}
              onChange={event => setIncompleteOnly(event.currentTarget.checked)}
            />
          </TableToolbar.Filters>
        </TableToolbar>
      ),
      renderRowActions: ({row}) => (
        <TableRowActions>
          <TableRowActions.View
            onClick={() =>
              navigate(
                PathUtility.DocumentManagerViewFile(row.original.documentId, 'content')
              )
            }
          />
        </TableRowActions>
      ),
    },
    totalCount,
    isError,
    isLoading,
    isFetching,
    onRefresh: refetch,
  });

  return <>{table}</>;
};
